import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowUpRight, Check, CircleCheck, Clock3, Leaf, Pencil, Search, ShieldCheck, X } from 'lucide-react';
import { toast } from 'sonner';
import { ActNow } from '../components/act-now';
import { api, formatMoney, type Environment, type Session } from '../api';
import type { JsonDTO } from '../../../src/domain/json';
import type { CollectionsView } from '../../../src/application/collections';
import { caseStatusLabels, type CaseStatus } from '../../../src/domain/collections/cases';
import { Button } from '../components/ui/button';
import { StuckStrip } from '../components/stuck-strip';
import { ago, formatDay, MoneyLines } from '../components/desk';
import { heldBlockers, useWorkDecisions, WorkItemBody, type WorkItem } from '../components/work-item';
import { actionLabel, actionTone, canJoin, compatible, keyCommand } from '../needs-you';
import { useAgentName } from '../agent-name';
import { AskAbout } from '../amy/ask-about';
import './collections.css';

type View = JsonDTO<CollectionsView>;
type Case = View['cases'][number];
type Tab = CaseStatus | 'all';
/** Needs you first: the page opens on what only a person can do, then follows the life of a case to Settled. */
const tabs: Tab[] = ['needs_you', 'flagged', 'scheduled', 'waiting', 'paused', 'other', 'settled', 'all'];
const tabLabel = (tab: Tab) => tab === 'all' ? 'All accounts' : caseStatusLabels[tab];
const kindLabels: Record<WorkItem['kind'], string> = { approval: 'Draft', reply: 'Reply', task: 'Task' };
type Density = 'detailed' | 'compact';

function readTab(): Tab | null {
  const raw = new URLSearchParams(window.location.hash.split('?')[1] ?? '').get('status');
  // Drafts joined Needs you (2026-11-01); old ?status=drafted links still land on them.
  const value = raw === 'drafted' ? 'needs_you' : raw;
  return value && (tabs as string[]).includes(value) ? value as Tab : null;
}
function writeHash(caseId: string | null, tab: Tab) {
  const path = caseId ? `collections/${encodeURIComponent(caseId)}` : 'collections';
  window.history.replaceState(null, '', `#${path}${tab === 'all' ? '' : `?status=${tab}`}`);
}
function moneyText(values: { minor: string; currency: string }[]) {
  return values.map(v => formatMoney(v.minor, v.currency)).join(' + ');
}
function sumMoney(lists: { minor: string; currency: string }[][]) {
  const totals = new Map<string, bigint>();
  for (const list of lists) for (const v of list) totals.set(v.currency, (totals.get(v.currency) ?? 0n) + BigInt(v.minor));
  return [...totals].filter(([, m]) => m !== 0n).map(([currency, minor]) => ({ currency, minor: minor.toString() }));
}
const requestNames: Record<string, string> = { promise: 'promise to pay', documents: 'documents', dispute: 'dispute',
  payment_claim: 'says it is paid', question: 'question', other: 'other' };
/** The thing to decide, in one line: the draft's subject and recipient, what the reply says, or the task. */
function itemLine(i: WorkItem) {
  if (i.kind === 'approval' && i.approval) {
    const to = i.approval.draft.to;
    return `“${i.approval.draft.subject}” to ${to[0] ?? 'the billing contact'}${to.length > 1 ? ` and ${to.length - 1} more` : ''}`;
  }
  if (i.kind === 'reply' && i.reply) {
    const asks = i.reply.requests ?? [];
    const lead = i.reply.summary || i.reply.subject;
    return asks.length > 1 ? `${lead} · ${asks.length} requests: ${asks.map(r => requestNames[r.type] ?? r.type).join(', ')}` : lead;
  }
  return i.task?.title ?? i.want;
}
/** "Today", "Nov 3", or nothing when Amy has no date for it. */
function nextWhen(c: Case, asOf: string) {
  if (!c.next.date) return null;
  return c.next.date.slice(0, 10) <= asOf.slice(0, 10) ? 'Today' : formatDay(c.next.date, asOf);
}

export function Collections({ session, initialId = null }: { session: Session; initialId?: string | null }) {
  const agent = useAgentName();
  const [view, setView] = useState<View | null>(null); const [environment, setEnvironment] = useState<Environment | null>(null);
  const [loadError, setLoadError] = useState('');
  const [tab, setTab] = useState<Tab | null>(readTab);
  const [query, setQuery] = useState('');
  const [density, setDensity] = useState<Density>(() => localStorage.getItem('alder-collections-density') === 'compact' ? 'compact' : 'detailed');
  const [openId, setOpenId] = useState<string | null>(null); const [cursorId, setCursorId] = useState<string | null>(null);
  const [itemKey, setItemKey] = useState<string | null>(null); const [picked, setPicked] = useState<string[]>([]);
  const [refused, setRefused] = useState<string[]>([]);
  const rows = useRef(new Map<string, HTMLButtonElement>()); const drawerHeading = useRef<HTMLHeadingElement>(null);
  const polls = useRef(0); const rejectArmed = useRef({ key: '', at: 0 }); const deepLink = useRef(initialId);
  const canDecide = session.actor.roles.some(role => ['owner', 'approver'].includes(role));

  const reload = useCallback(async () => {
    const [next, env] = await Promise.all([api<View>('/app-api/collections'), api<Environment>('/app-api/environment')]);
    setView(next); setEnvironment(env); setLoadError('');
  }, []);
  useEffect(() => {
    const refresh = () => { void reload().catch(e => setLoadError(e instanceof Error ? e.message : 'This page could not load.')); };
    refresh(); window.addEventListener('alder:state-changed', refresh);
    return () => window.removeEventListener('alder:state-changed', refresh);
  }, [reload]);
  // Amy is still writing some recommendations: ask again shortly, a few times at most.
  useEffect(() => {
    if (!view?.refining || polls.current >= 5) return;
    const timer = setTimeout(() => { polls.current++; void reload().catch(() => {}); }, 3000);
    return () => clearTimeout(timer);
  }, [view, reload]);
  useEffect(() => { localStorage.setItem('alder-collections-density', density); }, [density]);

  const items = useMemo(() => view?.items ?? [], [view]);
  const itemsByKey = useMemo(() => new Map(items.map(i => [i.key, i])), [items]);
  const cases = useMemo(() => view?.cases ?? [], [view]);
  const work = useWorkDecisions({ items, actorId: session.actor.id, shadow: view?.delivery.shadow ?? false, environment, reload });
  // Gmail-style: an approval that waits in its Undo window offers Undo for ten seconds; the bar above keeps it after that.
  const undoKey = work.undo ? `${work.undo.id}:${work.undo.revision}` : null;
  // The toast outlives the render that made it (the approval is still reloading then), so it calls the latest Undo.
  const undoApproval = useRef(work.undoApproval); undoApproval.current = work.undoApproval;
  useEffect(() => {
    if (!undoKey) return;
    // It lasts as long as the approval can still be undone, and not a moment longer.
    const duration = (work.undo?.until ?? 0) - Date.now();
    if (duration <= 0) return;
    const id = toast(work.notice || 'Approved.', { duration, action: { label: 'Undo', onClick: () => void undoApproval.current() } });
    return () => { toast.dismiss(id); };
    // One toast per approval: the notice text and the callback follow the approval they belong to.
  }, [undoKey]);

  // First load: open a deep link (a case, or an item key from an old #needs-you link) on a tab that shows it; with no tab
  // in the link, land on the first busy one.
  useEffect(() => {
    if (!view) return;
    const link = deepLink.current; deepLink.current = null;
    const target = link ? cases.find(c => c.id === link || c.itemKeys.includes(link)) : null;
    if (target) {
      setTab(t => t === 'all' || t === target.status ? t : target.status); setOpenId(target.id); setCursorId(target.id);
      setItemKey(target.itemKeys.includes(link!) ? link : target.itemKeys[0] ?? null);
      return;
    }
    setTab(t => t ?? (view.counts.needs_you ? 'needs_you' : view.counts.flagged ? 'flagged' : 'all'));
  }, [view, cases]);

  const activeTab: Tab = tab ?? 'needs_you';
  const needle = query.trim().toLowerCase();
  const visible = useMemo(() => cases.filter(c => (activeTab === 'all' || c.status === activeTab)
    && (!needle || c.customerName.toLowerCase().includes(needle)
      || c.invoiceNumbers.some(n => n.toLowerCase().includes(needle)))),
  [cases, activeTab, needle]);
  const groups = useMemo(() => {
    const order = activeTab === 'all' ? tabs.filter((t): t is CaseStatus => t !== 'all') : [activeTab];
    return order.map(status => ({ status, list: visible.filter(c => c.status === status) })).filter(g => g.list.length);
  }, [visible, activeTab]);
  const ordered = useMemo(() => groups.flatMap(g => g.list), [groups]);
  const open = cases.find(c => c.id === openId) ?? null;
  const openItems = (open?.itemKeys ?? []).map(k => itemsByKey.get(k)).filter((i): i is WorkItem => Boolean(i));
  const item = openItems.find(i => i.key === itemKey) ?? openItems[0] ?? null;

  const linkId = item && open ? item.key : openId;
  useEffect(() => { if (view && tab) writeHash(linkId, activeTab); }, [view, tab, linkId, activeTab]);
  useEffect(() => { setPicked(keys => keys.filter(k => itemsByKey.get(k)?.status === 'pending')); }, [itemsByKey]);

  const pickedItems = picked.map(k => itemsByKey.get(k)).filter((i): i is WorkItem => Boolean(i));
  // A case's drafts that can be approved together with the selection: pending, batchable, at the selection's policy step.
  const caseDrafts = (c: Case) => {
    if (!canDecide) return [];
    const drafts = c.itemKeys.map(k => itemsByKey.get(k)).filter((i): i is WorkItem =>
      Boolean(i && i.kind === 'approval' && i.status === 'pending' && i.batchKey && !work.voted(i)));
    const step = pickedItems[0]?.batchKey ?? drafts[0]?.batchKey;
    return drafts.filter(i => i.batchKey === step);
  };
  const pickCase = (c: Case) => {
    const drafts = caseDrafts(c); const keys = drafts.map(i => i.key);
    if (!drafts.length) return;
    setPicked(current => keys.every(k => current.includes(k)) ? current.filter(k => !keys.includes(k))
      : drafts.every(i => canJoin(pickedItems, i)) ? [...new Set([...current, ...keys])] : current);
  };

  /** Beside the table, focus stays on the row so j/k keep moving; where the drawer covers the table, focus moves into it. */
  const overlay = () => window.matchMedia('(max-width: 1180px)').matches;
  function openCase(c: Case, focus = overlay()) {
    setOpenId(c.id); setCursorId(c.id); setItemKey(c.itemKeys[0] ?? null); work.clear(); setRefused([]);
    if (focus) setTimeout(() => drawerHeading.current?.focus(), 0);
  }
  function closeDrawer() {
    const id = openId; setOpenId(null); work.clear();
    if (id) setTimeout(() => rows.current.get(id)?.focus(), 0);
  }
  function chooseTab(next: Tab) { setTab(next); setPicked([]); }
  const approveTogether = async () => {
    const outcome = await work.approveTogether(pickedItems);
    if (!outcome) return;
    setPicked([]); setRefused(outcome.filter(r => !r.ok).map(r => r.message));
  };

  // Keyboard first: j/k move, Enter opens, Esc closes; on an open draft a approve, e edit, r reject (twice), s snooze, x select.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (work.editing) return;
      // Esc and Enter go through the same filter as the letter keys: never while typing or with a modifier held.
      const plain = keyCommand({ key: 'j', metaKey: event.metaKey, ctrlKey: event.ctrlKey, altKey: event.altKey,
        target: event.target }) !== null;
      if (event.key === 'Escape' && openId && plain) { event.preventDefault(); closeDrawer(); return; }
      const onControl = Boolean((event.target as HTMLElement | null)?.closest?.('a, button, summary, [role="tab"], [tabindex]'));
      if (event.key === 'Enter' && plain && !openId && !onControl) {
        const target = ordered.find(c => c.id === cursorId) ?? ordered[0];
        if (target) { event.preventDefault(); openCase(target, true); }
        return;
      }
      const command = keyCommand(event); if (!command || !ordered.length) return;
      // A letter typed on a button, link, menu or dialog belongs to it; only the row's own control passes keys through.
      const focused = event.target as HTMLElement | null;
      const onRow = Boolean(focused?.closest?.('.col-open'));
      if (!onRow && focused?.closest?.('a, button, summary, select, [role="tab"], [role="menu"], [role="menuitem"], [role="dialog"], [role="listbox"], [role="option"]')) return;
      event.preventDefault();
      const found = ordered.findIndex(c => c.id === (openId ?? cursorId));
      const index = Math.max(0, found);
      if (command === 'next' || command === 'previous') {
        const next = found < 0 ? ordered[0]! : ordered[Math.min(ordered.length - 1, Math.max(0, index + (command === 'next' ? 1 : -1)))]!;
        setCursorId(next.id);
        if (openId && overlay()) { openCase(next, true); return; }
        if (openId) openCase(next, false);
        rows.current.get(next.id)?.focus(); return;
      }
      // Deciding needs the row that is visibly highlighted: never a fallback to the first row.
      if (!open && found < 0) return;
      const current = open ?? ordered[index]!;
      if (command === 'select') { pickCase(current); return; }
      // With the drawer closed, the keys act on the highlighted row's first item, the one its recommendation is about.
      const target = open ? item : itemsByKey.get(current.itemKeys[0] ?? '') ?? null;
      if (!open && command === 'edit' && target?.kind === 'approval' && canDecide) { editFrom(current, target); return; }
      if (!target || !canDecide || work.busy) return;
      decideByKey(command, target);
    };
    window.addEventListener('keydown', onKey); return () => window.removeEventListener('keydown', onKey);
  });
  function decideByKey(command: NonNullable<ReturnType<typeof keyCommand>>, item: WorkItem) {
    const pendingDraft = item.kind === 'approval' && item.status === 'pending';
    if (command === 'approve' && pendingDraft && !work.voted(item)) void work.decideApproval(item, 'approve');
    if (command === 'edit' && item.kind === 'approval') work.setEditing(true);
    if (command === 'snooze' && pendingDraft) void work.decideApproval(item, 'snooze');
    if (command === 'snooze' && item.kind === 'task') void work.decideTask(item, 'snooze');
    if (command === 'reject' && item.kind === 'approval') {
      const armed = rejectArmed.current;
      if (armed.key === item.key && Date.now() - armed.at < 3000) { rejectArmed.current = { key: '', at: 0 }; void work.decideApproval(item, 'reject'); }
      else { rejectArmed.current = { key: item.key, at: Date.now() }; work.setNotice(`Press r again to reject the draft for ${item.customerName}.`); }
    }
  }
  /** Edit lives in the drawer, beside the whole email: open the case on that draft, then start editing. */
  function editFrom(c: Case, target: WorkItem) {
    openCase(c, true); setItemKey(target.key);
    setTimeout(() => work.setEditing(true), 0);
  }

  const shadow = view?.delivery.shadow ?? false;
  const banner = shadow ? 'Shadow mode: approving records your decision. Nothing is emailed to customers.'
    : view?.delivery.mode === 'mock' ? 'Demo workspace: approved emails land in the demo mailbox, never with a customer.'
      : view?.delivery.mode === 'redirect' ? `Approved emails are redirected to ${environment?.redirectTo ?? 'a test inbox'}.` : null;
  const working = cases.filter(c => c.status !== 'settled');
  const summary = !view ? 'Gathering the caseload.' : [
    `${agent} is working ${working.length} ${working.length === 1 ? 'account' : 'accounts'}`
      + (working.length ? ` with ${moneyText(sumMoney(working.map(c => c.balances)))} open` : '') + '.',
    view.items.length ? `${view.items.length} ${view.items.length === 1 ? 'item needs' : 'items need'} you.` : 'Nothing needs you.',
    view.counts.flagged ? `${view.counts.flagged} flagged.` : '',
  ].filter(Boolean).join(' ');

  function row(c: Case) {
    const drafts = caseDrafts(c); const checked = drafts.length > 0 && drafts.every(i => picked.includes(i.key));
    const joinable = checked || (drafts.length > 0 && drafts.every(i => canJoin(pickedItems, i)));
    const when = view ? nextWhen(c, view.asOf) : null;
    const first = itemsByKey.get(c.itemKeys[0] ?? '');
    return <Fragment key={c.id}><tr className={`col-row ${c.id === openId ? 'open' : ''} ${c.id === cursorId ? 'cursor' : ''} ${first && density === 'detailed' ? 'has-rec' : ''}`}
      onClick={event => { if (!(event.target as HTMLElement).closest('input, a, button')) openCase(c); }}>
      <td className="col-pick">{drafts.length > 0 && <input type="checkbox"
        aria-label={`Select ${c.customerName}'s ${drafts.length === 1 ? 'draft' : `${drafts.length} drafts`} to approve together`}
        checked={checked} disabled={!joinable || work.busy} onChange={() => pickCase(c)}
        title={joinable ? undefined : 'Only drafts at the same reminder step can be approved together'} />}</td>
      <td className="col-who">
        <button type="button" className="col-open" aria-expanded={c.id === openId}
          ref={node => { if (node) rows.current.set(c.id, node); else rows.current.delete(c.id); }}
          onFocus={() => setCursorId(c.id)}
          onClick={() => openCase(c)}>{c.customerName}</button>
        {c.customerId && <AskAbout customerId={c.customerId} name={c.customerName} />}
        {density === 'detailed' && <span className="col-situation">{c.situation}</span>}
      </td>
      <td><span className={`col-pill s-${c.status}`}>{caseStatusLabels[c.status]}</span></td>
      <td className="col-next"><span>{c.next.summary}</span>{c.next.status === 'you' ? <small className="col-yours">Needs your decision</small>
        : when && <small>{when}</small>}</td>
      <td className="num"><MoneyLines values={c.balances} /></td>
      <td className="num col-late">{c.overdue.length ? <><MoneyLines values={c.overdue} />
        <small>{c.oldestDaysOverdue} days late</small></> : <span className="muted">–</span>}</td>
      <td className="col-contact">{c.lastContact && view ? <>{ago(c.lastContact.at, view.asOf)}
        <small>{c.lastContact.direction === 'in' ? 'They wrote' : 'We emailed'}</small></> : <span className="muted" title="No emails yet">–</span>}</td>
    </tr>{first && density === 'detailed' && recommendation(c, first)}</Fragment>;
  }

  /**
   * The recommendation-first line under a row that waits on a person (#282): what the agent recommends and why, the thing
   * to decide, and the decision itself. Each button is the drawer's own decision, through the same route and checks.
   */
  function recommendation(c: Case, first: WorkItem) {
    const more = c.itemKeys.length - 1; const r = first.recommendation;
    const pendingDraft = first.kind === 'approval' && first.status === 'pending';
    const busy = work.busy;
    const primary = !canDecide ? null : pendingDraft ? work.voted(first) ? 'Approval recorded' : 'Approve' : first.kind === 'task'
      ? first.task?.kind === 'pause_request' ? 'Approve pause' : 'Mark done' : first.kind === 'reply' ? 'Read the reply' : 'Open';
    return <tr className={`col-rec ${c.id === openId ? 'open' : ''}`} data-key={first.key}
      onClick={event => { if (!(event.target as HTMLElement).closest('a, button')) openCase(c); }}>
      <td className="col-pick" />
      <td colSpan={6}>
        <div className="col-rec-body">
          <p className="col-rec-line" data-action={actionLabel(r.action)}><Leaf size={13} strokeWidth={1.8} aria-hidden /><span className="col-rec-who">{agent} recommends</span>
            {actionLabel(r.action) !== primary && <span className={`ny-chip tone-${actionTone(r.action)}`}>{actionLabel(r.action)}</span>}
            <span className="col-rec-reason">{r.reason}</span></p>
          {(more > 0 || itemLine(first) !== c.next.summary) && <p className="col-rec-item">
            <span className="col-kind">{kindLabels[first.kind]}</span><span>{itemLine(first)}</span>
            {more > 0 && <button type="button" className="col-rec-more" onClick={() => openCase(c)}>
              {more === 1 ? '1 more thing needs you' : `${more} more things need you`}</button>}</p>}
          {canDecide && <div className="col-rec-actions" role="group" aria-label={`Decide for ${c.customerName}`}>
            {pendingDraft && <>
              <Button size="sm" disabled={busy || work.voted(first)} onClick={() => void work.decideApproval(first, 'approve')}>
                <Check aria-hidden />{work.voted(first) ? 'Approval recorded' : 'Approve'}</Button>
              <Button size="sm" variant="outline" disabled={busy} onClick={() => editFrom(c, first)}><Pencil aria-hidden />Edit</Button>
              <Button size="sm" variant="ghost" disabled={busy} onClick={() => void work.decideApproval(first, 'reject')}><X aria-hidden />Reject</Button>
              <Button size="sm" variant="ghost" disabled={busy} onClick={() => void work.decideApproval(first, 'snooze')}><Clock3 aria-hidden />Snooze 1h</Button>
            </>}
            {first.kind === 'task' && <>
              <Button size="sm" disabled={busy} onClick={() => void work.decideTask(first, first.task?.kind === 'pause_request' ? 'approve_pause' : 'done')}>
                <Check aria-hidden />{first.task?.kind === 'pause_request' ? 'Approve pause' : 'Mark done'}</Button>
              <Button size="sm" variant="ghost" disabled={busy} onClick={() => void work.decideTask(first, 'snooze')}><Clock3 aria-hidden />Snooze a day</Button>
            </>}
            {(first.kind === 'reply' || (first.kind === 'approval' && !pendingDraft)) && <Button size="sm" variant="outline" onClick={() => openCase(c, true)}>
              {first.kind === 'reply' ? 'Read the reply' : 'Open'}</Button>}
          </div>}
        </div>
      </td>
    </tr>;
  }

  function drawer(c: Case) {
    const when = view ? nextWhen(c, view.asOf) : null;
    const held = c.blockers.filter(b => b.code !== 'needs_review');
    return <aside className="col-drawer" aria-labelledby="col-drawer-title" data-case={c.id}>
      <header className="col-drawer-head">
        <div>
          <span className={`col-pill s-${c.status}`}>{caseStatusLabels[c.status]}</span>
          <h2 id="col-drawer-title" ref={drawerHeading} tabIndex={-1}>{c.customerName}</h2>
          <p className="col-drawer-figures">
            {c.balances.length > 0 && <span><b>{moneyText(c.balances)}</b> open</span>}
            {c.overdue.length > 0 && <span>{moneyText(c.overdue)} overdue, oldest {c.oldestDaysOverdue} days</span>}
            {c.habit && <span>{c.habit.label}</span>}
          </p>
        </div>
        <Button variant="ghost" size="icon" aria-label="Close" onClick={closeDrawer}><X aria-hidden /></Button>
      </header>
      <div className="col-drawer-body">
        <section className="col-story" aria-label="Where this account stands">
          <p className="col-story-line">{c.situation}</p>
          {!openItems.length && <p className="col-story-next"><span>{agent}&rsquo;s next step</span>{c.next.summary}{when && <> · {when}</>}</p>}
          {held.length > 0 && <StuckStrip blockers={held} />}
        </section>
        {openItems.length > 1 && <div className="col-items" role="tablist" aria-label="Waiting on you for this account">
          {openItems.map(i => <button key={i.key} type="button" role="tab" aria-selected={i.key === item?.key}
            className={i.key === item?.key ? 'active' : undefined} onClick={() => { setItemKey(i.key); work.clear(); }}>
            <span>{kindLabels[i.kind]}</span><em>{i.want}</em>
            <small>{[i.invoices.map(n => n.number).join(', '), i.amount && formatMoney(i.amount.minor, i.amount.currency)]
              .filter(Boolean).join(' · ')}</small></button>)}
        </div>}
        {item ? <section className="col-item" aria-labelledby="col-item-title" data-key={item.key}>
          <h3 id="col-item-title"><span className="col-kind">{kindLabels[item.kind]}</span>{item.want}</h3>
          {heldBlockers(item).length > 0 && held.length === 0 && <StuckStrip blockers={heldBlockers(item)} />}
          <WorkItemBody item={item} work={work} canDecide={canDecide} />
        </section> : <section className="col-quiet">
          <span className="ny-amy-mark" aria-hidden><Leaf size={15} strokeWidth={1.8} /></span>
          <p>{c.status === 'flagged' ? 'This one needs a person’s call. Follow up now, or open the customer file.'
            : c.status === 'settled' ? 'Paid up. Nothing here needs you.' : `Nothing here needs you. ${agent} has it.`}</p>
        </section>}
        {!item && c.status === 'flagged' && c.customerId && session.actor.roles.includes('owner') && <ActNow customerId={c.customerId} />}
      </div>
      {c.customerId && <footer className="col-drawer-foot">
        <a href={`#customers/${encodeURIComponent(c.customerId)}`}>Open the customer file<ArrowUpRight size={14} aria-hidden /></a>
      </footer>}
    </aside>;
  }

  return <main id="main-content" className={`queue-page col-page ${open ? 'drawer-open' : ''}`}>
    {banner && <div className={`ny-banner ${shadow ? 'shadow' : ''}`} role="note"><ShieldCheck size={15} aria-hidden />{banner}</div>}
    <header className="page-heading col-heading">
      <div><h1>Collections<span className="heading-dot" aria-hidden="true">.</span></h1><p>{summary}</p></div>
      <div className="col-tools">
        <label className="col-search"><Search size={14} aria-hidden /><span className="sr-only">Search customers or invoices</span>
          <input type="search" placeholder="Customer or invoice" value={query} onChange={e => setQuery(e.target.value)} /></label>
        <div className="col-density" role="group" aria-label="Row density">
          {(['detailed', 'compact'] as const).map(d => <button key={d} type="button" aria-pressed={density === d}
            onClick={() => setDensity(d)}>{d === 'detailed' ? 'Detailed' : 'Compact'}</button>)}
        </div>
      </div>
    </header>
    {view && view.workspace.length > 0 && <StuckStrip blockers={view.workspace} className="col-workspace" />}
    {(loadError || work.error) && <div className="alert error" role="alert"><X size={16} aria-hidden /><span>{loadError || work.error}</span></div>}
    <div className="ny-live" role="status" aria-live="polite">{work.notice && <><CircleCheck size={15} aria-hidden /><span>{work.notice}</span>
      {work.undo && <Button variant="outline" size="sm" disabled={work.busy} onClick={() => void work.undoApproval()}>Undo</Button>}</>}</div>
    {refused.length > 0 && <ul className="ny-results col-refused" aria-label="Not approved">{refused.map(m =>
      <li key={m} className="refused"><X size={15} aria-hidden /><span>{m}</span></li>)}</ul>}
    <nav className="col-tabs" role="tablist" aria-label="Cases by status">
      {tabs.map(t => { const n = !view ? null : t === 'all' ? cases.length : t === 'needs_you' ? view.items.length : view.counts[t];
        return <button key={t} type="button" role="tab" aria-selected={activeTab === t} className={`${activeTab === t ? 'active' : ''} ${n === 0 ? 'empty' : ''}`}
          onClick={() => chooseTab(t)}>{tabLabel(t)}{n !== null && <span>{n}</span>}</button>; })}
    </nav>
    {picked.length > 0 && <div className="col-batch" role="group" aria-label="Approve together">
      <span>{picked.length} {picked.length === 1 ? 'draft' : 'drafts'} selected
        {pickedItems.some(i => i.amount) && <> · {moneyText(sumMoney(pickedItems.map(i => i.amount ? [i.amount] : [])))}</>}</span>
      <Button size="sm" variant="ghost" onClick={() => setPicked([])} disabled={work.busy}>Clear</Button>
      <Button size="sm" disabled={work.busy || !compatible(pickedItems)} onClick={() => void approveTogether()}><Check aria-hidden />
        Approve {picked.length}</Button>
    </div>}
    <div className="col-layout">
      <div className="col-table-wrap">
        {!view ? <p className="ny-muted col-empty" role="status">Gathering the caseload…</p>
          : !ordered.length ? <div className="col-empty">
            <span className="ny-amy-mark" aria-hidden><Leaf size={16} strokeWidth={1.6} /></span>
            <p>{needle ? 'No account matches that search.' : activeTab === 'needs_you' ? 'Nothing needs you right now.'
              : `No accounts are ${tabLabel(activeTab).toLowerCase()}.`}</p></div>
            : <table className={`col-table ${density}`}>
              <thead><tr><th className="col-pick"><span className="sr-only">Select</span></th><th>Account</th><th>Status</th>
                <th>Next step</th><th className="num">Open</th><th className="num">Overdue</th><th>Last contact</th></tr></thead>
              {groups.map(g => <tbody key={g.status}>
                {activeTab === 'all' && <tr className="col-group"><th colSpan={7} scope="rowgroup">
                  <span>{caseStatusLabels[g.status]}<em>{g.list.length}</em></span>
                  <b>{moneyText(sumMoney(g.list.map(c => c.balances)))}</b></th></tr>}
                {g.list.map(row)}
              </tbody>)}
              {activeTab !== 'all' && ordered.length > 0 && <tfoot><tr><td colSpan={4}>{ordered.length} {ordered.length === 1 ? 'account' : 'accounts'}</td>
                <td className="num" colSpan={1}>{moneyText(sumMoney(ordered.map(c => c.balances))) || '–'}</td><td colSpan={2} /></tr></tfoot>}
            </table>}
        <p className="ny-keys col-keys" aria-label="Keyboard shortcuts"><kbd>j</kbd><kbd>k</kbd> move <kbd>Enter</kbd> open
          <kbd>a</kbd> approve <kbd>e</kbd> edit <kbd>r</kbd> reject <kbd>s</kbd> snooze <kbd>x</kbd> select <kbd>Esc</kbd> close</p>
      </div>
      {open && drawer(open)}
    </div>
  </main>;
}
